// Meilenstein-Übersicht — chronologische Zusammenfassung aller als Meilenstein
// markierten Aufgaben (tasks.is_milestone) eines Projekts.
//
// Datenquelle ist der bereits geladene Projekt-Detail-State (tasksByPhase); es
// wird kein zusätzlicher Fetch gemacht — die Ansicht bleibt automatisch mit dem
// SSE-Stream / den Mutationen synchron. Gruppiert nach Überfällig / Anstehend /
// Erledigt; innerhalb nach Termin (deadline ?? end_date ?? start_date) sortiert.
//
// #876 drag & drop — deliberately NOT implemented in this view. Its three
// sections (Überfällig / Anstehend / Erledigt) are derived buckets computed
// from each task's date + done-state, not drop targets a task can be placed
// into — there is no day-grid here for a row to land on. Milestone dates are
// already drag-editable where a real drop target exists: GanttView's
// timeline lets you drag a milestone to any day on an arbitrary range (see
// GanttView.tsx's handleMove). Adding a second, more limited date-drag
// surface here (e.g. a fixed today+N strip) would just be a worse copy of
// that capability, so this view stays click-to-open only.

import { useMemo, useState } from "preact/hooks";
import { Flag } from "lucide-preact";
import type { Phase, ProjectWithCounts, Task } from "@juuplan/shared";
import { formatDateShort, localTodayIso } from "../lib/format";
import { UserAvatar } from "../components/UserAvatar";
import { TaskModal } from "../components/TaskModal";

interface Props {
  project: ProjectWithCounts;
  tasksByPhase: Record<number, Task[]>;
  readOnly?: boolean;
}

/** Effective milestone date: deadline first, then end_date, then start_date. */
function milestoneDate(t: Task): string | null {
  return t.deadline ?? t.end_date ?? t.start_date ?? null;
}

function isDone(t: Task): boolean {
  return t.status === "done" || t.completed_at !== null;
}

interface Bucket {
  key: "overdue" | "upcoming" | "done";
  label: string;
  hint: string;
  items: Task[];
}

export function MilestonesView(props: Props): preact.JSX.Element {
  const { project, tasksByPhase, readOnly = false } = props;
  const [openTaskId, setOpenTaskId] = useState<number | null>(null);

  const phaseById = useMemo(() => {
    const m: Record<number, Phase> = {};
    for (const p of project.phases) m[p.id] = p;
    return m;
  }, [project.phases]);

  const allTasks = useMemo(
    () => Object.values(tasksByPhase).flat(),
    [tasksByPhase]
  );

  const milestones = useMemo(
    () => allTasks.filter((t) => t.is_milestone),
    [allTasks]
  );

  const buckets = useMemo<Bucket[]>(() => {
    const today = localTodayIso();
    const overdue: Task[] = [];
    const upcoming: Task[] = [];
    const done: Task[] = [];

    for (const t of milestones) {
      if (isDone(t)) {
        done.push(t);
        continue;
      }
      const d = milestoneDate(t);
      if (d !== null && d < today) overdue.push(t);
      else upcoming.push(t);
    }

    // Overdue: most-overdue first (ascending date). Upcoming: soonest first,
    // undated last. Done: recently-due first (descending date).
    const asc = (a: Task, b: Task): number =>
      (milestoneDate(a) ?? "9999").localeCompare(milestoneDate(b) ?? "9999");
    overdue.sort(asc);
    upcoming.sort(asc);
    done.sort((a, b) =>
      (milestoneDate(b) ?? "").localeCompare(milestoneDate(a) ?? "")
    );

    return [
      { key: "overdue", label: "Überfällig", hint: "Termin überschritten, noch offen", items: overdue },
      { key: "upcoming", label: "Anstehend", hint: "Geplant und offen", items: upcoming },
      { key: "done", label: "Erledigt", hint: "Abgeschlossene Meilensteine", items: done },
    ];
  }, [milestones]);

  const openTask = allTasks.find((t) => t.id === openTaskId) ?? null;

  if (milestones.length === 0) {
    return (
      <div class="rounded-lg border border-dashed border-[color:var(--color-outline-variant)] px-4 py-12 text-center">
        <Flag
          size={24}
          aria-hidden="true"
          class="mx-auto mb-3 text-[color:var(--color-on-surface-variant)]/60"
        />
        <p class="text-sm font-medium text-[color:var(--color-on-surface)]">
          Noch keine Meilensteine
        </p>
        <p class="mt-1 text-xs text-[color:var(--color-on-surface-variant)]">
          Markiere eine Aufgabe als Meilenstein oder lege über „+ Neu → Meilenstein" einen an.
        </p>
      </div>
    );
  }

  return (
    <div class="flex flex-col gap-6">
      {buckets.map((b) =>
        b.items.length === 0 ? null : (
          <section key={b.key} aria-label={b.label}>
            <header class="mb-2 flex items-baseline gap-2">
              <h3
                class={`text-sm font-semibold ${
                  b.key === "overdue"
                    ? "text-[color:var(--color-error)]"
                    : "text-[color:var(--color-on-surface)]"
                }`}
              >
                {b.label}
              </h3>
              <span class="inline-flex items-center justify-center rounded-full bg-[color:var(--color-surface-container-high)] px-2 py-0.5 text-label font-semibold tabular-nums text-[color:var(--color-on-surface-variant)]">
                {b.items.length}
              </span>
              <span class="text-label text-[color:var(--color-on-surface-variant)]/70">
                {b.hint}
              </span>
            </header>
            <ul class="flex flex-col gap-1.5">
              {b.items.map((t) => (
                <MilestoneRow
                  key={t.id}
                  task={t}
                  phase={phaseById[t.phase_id] ?? null}
                  overdue={b.key === "overdue"}
                  done={b.key === "done"}
                  onOpen={() => setOpenTaskId(t.id)}
                />
              ))}
            </ul>
          </section>
        )
      )}

      {openTask && (
        <TaskModal
          task={openTask}
          siblings={allTasks}
          projectId={project.id}
          phases={project.phases}
          onClose={() => setOpenTaskId(null)}
          onDeleted={() => setOpenTaskId(null)}
          readOnly={readOnly}
        />
      )}
    </div>
  );
}

function MilestoneRow({
  task,
  phase,
  overdue,
  done,
  onOpen,
}: {
  task: Task;
  phase: Phase | null;
  overdue: boolean;
  done: boolean;
  onOpen: () => void;
}): preact.JSX.Element {
  const d = milestoneDate(task);
  const primaryAssignee = task.primary_assignee_id;

  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        class="flex w-full items-center gap-3 rounded-md border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2.5 text-left hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-1 transition-colors"
        data-testid={`milestone-row-${task.id}`}
      >
        <Flag
          size={16}
          aria-hidden="true"
          class={
            done
              ? "text-[color:var(--color-on-surface-variant)]/50 shrink-0"
              : overdue
                ? "text-[color:var(--color-error)] shrink-0"
                : "text-[color:var(--color-primary)] shrink-0"
          }
        />
        <span
          class={`flex-1 min-w-0 truncate text-sm ${
            done
              ? "text-[color:var(--color-on-surface-variant)] line-through"
              : "text-[color:var(--color-on-surface)] font-medium"
          }`}
        >
          {task.ticket && (
            <span class="mr-1.5 text-label font-mono text-[color:var(--color-on-surface-variant)]">
              {task.ticket}
            </span>
          )}
          {task.title}
        </span>
        {phase && (
          <span
            class="hidden sm:inline-flex shrink-0 items-center gap-1 rounded-full border border-[color:var(--color-outline-variant)] px-2 py-0.5 text-label font-medium text-[color:var(--color-on-surface-variant)]"
            title={`Phase: ${phase.name}`}
          >
            <span
              class="inline-block size-1.5 rounded-full"
              style={{ background: phase.color ?? "var(--color-primary)" }}
              aria-hidden="true"
            />
            <span class="max-w-[120px] truncate">{phase.name}</span>
          </span>
        )}
        <span
          class={`shrink-0 text-xs tabular-nums ${
            overdue
              ? "text-[color:var(--color-error)] font-medium"
              : "text-[color:var(--color-on-surface-variant)]"
          }`}
        >
          {d ? formatDateShort(d) : "ohne Termin"}
        </span>
        {primaryAssignee !== null && (
          <UserAvatar userId={primaryAssignee} size={20} />
        )}
      </button>
    </li>
  );
}
